import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import { useState, useCallback } from 'react';

import { useGetReportingTemplateDownloadUrlLazyQueryGQLType } from '../requests/reporting.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';

export const useDownloadReportTemplateUrl = (params: {
  creditFacilityId: string;
  creditAgreementId: string;
  reportName: string;
  templateConfig?: {
    name: string;
    s3Path: string;
  };
}) => {
  const {
    creditFacilityId,
    templateConfig,
    creditAgreementId,
    reportName,
  } = params;

  const toast = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [
    getReportTemplateUrlQuery,
  ] = useGetReportingTemplateDownloadUrlLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    onCompleted: async ({ downloadUrl }) => {
      if (!templateConfig) {
        return;
      }

      const { name: templateName } = templateConfig;
      if (!downloadUrl) {
        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URL [template:${templateName}]`,
        });
        return;
      }

      try {
        const response = await axios({
          url: downloadUrl,
          method: 'GET',
          responseType: 'blob',
        });

        const fileBlob = new Blob([response.data], {
          type: response.data.type,
        });
        FileSaver.saveAs(fileBlob, templateName);
      } catch (_error) {
        toast({
          ...errorToastConfig,
          title: `Failed to download [template:${templateName}]`,
        });
      }
    },
  });

  const getReportTemplateDownload = useCallback(async () => {
    if (!templateConfig) {
      toast({
        ...errorToastConfig,
        title: 'Can not download non-existing template',
      });
      return;
    }

    setIsLoading(true);

    await getReportTemplateUrlQuery({
      variables: {
        creditFacilityId,
        creditAgreementId,
        reportName,
      },
    });

    setIsLoading(false);
  }, [
    creditFacilityId,
    creditAgreementId,
    reportName,
    getReportTemplateUrlQuery,
    templateConfig,
    toast,
  ]);

  return {
    getReportTemplateDownload,
    isLoading,
  };
};
